/**
 * Protected Route Components
 * Handles authentication and role-based access control
 * Security: each role can only access its own space
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LoadingSpinner = () => (
  <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#C9A24D]/20 border-t-[#C9A24D] rounded-full animate-spin mx-auto mb-4" />
      <p className="text-[#E5E5E5]/60">Chargement...</p>
    </div>
  </div>
);

/**
 * Get the home route for a user based on their roles
 */
function getRoleHome(hasRole) {
  if (hasRole('SUPERADMIN') || hasRole('ADMIN') || hasRole('DISPATCHER')) return '/admin';
  if (hasRole('PARTNER')) return '/partner/dashboard';
  if (hasRole('CHAUFFEUR') || hasRole('DRIVER')) return '/app';
  return '/client';
}

/**
 * ProtectedRoute - Requires authentication only
 */
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

/**
 * RoleProtectedRoute - Requires specific role(s), redirects unauthorized to their home
 */
export const RoleProtectedRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { isAuthenticated, loading, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;

  const hasAllowedRole = allowedRoles.some(role => hasRole(role));
  if (!hasAllowedRole) {
    const home = getRoleHome(hasRole);
    return <Navigate to={home} replace />;
  }

  // Ticket 6.1: Check module-level permission if specified
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

/**
 * PublicOnlyRoute - Redirects authenticated users to their home
 */
export const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading, hasRole } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (isAuthenticated) {
    const home = getRoleHome(hasRole);
    return <Navigate to={home} replace />;
  }

  return children;
};

export default ProtectedRoute;
